import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import FaqAccordion from "@/components/base/FaqAccordion";
import { contactInfo, faqs } from "@/mocks/site";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { usePageMeta } from "@/hooks/usePageMeta";

const subjects = ["提交事件线索", "内容纠错", "内容合作", "其他咨询"];

const contactChannels = [
  {
    icon: "ri-mail-line",
    label: "编辑与纠错",
    value: contactInfo.email,
    href: `mailto:${contactInfo.email}`,
    desc: "发现时间线有误、或想补充公开来源，发这里。",
  },
  {
    icon: "ri-lightbulb-line",
    label: "线索投稿",
    value: contactInfo.tipEmail,
    href: `mailto:${contactInfo.tipEmail}`,
    desc: "请附上可查证的公开链接，我们会在核对后回复。",
  },
  {
    icon: "ri-briefcase-line",
    label: "商务与转载",
    value: contactInfo.businessEmail,
    href: `mailto:${contactInfo.businessEmail}`,
    desc: "内容授权、栏目合作与转载申请请走这个邮箱。",
  },
];

const CONTACT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "联系我们 - 深夜黑料",
  description: "深夜黑料的联系渠道，可提交事件线索、内容纠错与合作咨询。",
  inLanguage: "zh-CN",
  url: "https://midnight-archive.example/contact",
};

export default function Contact() {
  const { status, error, successMessage, submit } = useFormSubmit({
    submitAddr: "https://readdy.ai/api/form/dah6jproh653ivfvnme0",
    successMessage: "已收到你的信息，我们会尽快回复。",
  });

  usePageMeta({
    title: "联系我们 | 深夜黑料 线索投稿与内容合作",
    description:
      "向深夜黑料提交事件线索、内容纠错或合作咨询。请附上可查证的公开来源，我们会在工作日一个工作日内回复。",
    keywords: "深夜黑料联系,事件线索投稿,内容纠错,内容合作",
    path: "/contact",
    jsonLd: CONTACT_JSONLD,
  });

  return (
    <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "联系我们" }]} />

      <header className="mt-6 max-w-3xl">
        <p className="flex items-center gap-3 text-xs tracking-[0.32em] text-accent-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
          CONTACT
        </p>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          联系我们
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          线索、纠错、合作，都可以从这里找到我们。提交时请尽量附上可查证的公开来源，这样核对会快很多。
        </p>
      </header>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-send-plane-line text-accent-500" />
              在线留言
            </h2>
            <p className="mt-2 text-xs text-foreground-500">
              带 <span className="text-primary-400">*</span> 的为必填项，留言请控制在 500 字以内。
            </p>

            <form id="contact-inquiry" data-readdy-form onSubmit={submit} className="mt-6 space-y-5" noValidate>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-sm text-foreground-700">
                    称呼 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors duration-200 focus:border-accent-500"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-sm text-foreground-700">
                    邮箱 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors duration-200 focus:border-accent-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="mb-2 block text-sm text-foreground-700">
                  咨询类型 <span className="text-primary-400">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors duration-200 focus:border-accent-500 cursor-pointer"
                >
                  <option value="" disabled>
                    请选择咨询类型
                  </option>
                  {subjects.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-2 block text-sm text-foreground-700">
                  留言内容 <span className="text-primary-400">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="请描述你想提交的线索或问题，并附上可查证的公开来源链接。"
                  className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors duration-200 focus:border-accent-500"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="form-verify-alt-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full whitespace-nowrap rounded-md bg-primary-600 px-7 py-3 text-sm font-medium text-foreground-950 transition-colors duration-200 hover:bg-primary-500 disabled:opacity-60 sm:w-auto cursor-pointer"
                >
                  {status === "submitting" ? "正在提交…" : "提交留言"}
                </button>
                <p className="text-xs text-foreground-500">{contactInfo.responseTime}</p>
              </div>

              {status === "success" && (
                <p className="flex items-start gap-2 rounded-md border border-secondary-800/60 bg-secondary-950/60 px-4 py-3 text-sm text-secondary-300">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base" />
                  {successMessage}
                </p>
              )}
              {status === "error" && (
                <p className="flex items-start gap-2 rounded-md border border-primary-800/60 bg-primary-950/60 px-4 py-3 text-sm text-primary-300">
                  <i className="ri-error-warning-line mt-0.5 text-base" />
                  {error}
                </p>
              )}
            </form>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="space-y-4">
            {contactChannels.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-950 text-accent-400">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading text-sm font-semibold text-foreground-950">{item.label}</p>
                    <a
                      href={item.href}
                      rel="nofollow"
                      className="mt-1 block break-all text-sm text-accent-400 transition-colors duration-200 hover:text-accent-300"
                    >
                      {item.value}
                    </a>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg border border-accent-900/50 bg-gradient-to-b from-primary-950 to-background-100 p-5">
            <p className="flex items-center gap-2 text-xs tracking-[0.2em] text-accent-400">
              <i className="ri-shield-check-line text-base" />
              投稿提示
            </p>
            <ul className="mt-3 space-y-2 text-xs leading-relaxed text-foreground-600">
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-accent-500" />
                请勿提交未公开的私人信息或无法核实的匿名爆料。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-accent-500" />
                附上公开报道或官方公告链接，核对速度会明显更快。
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-accent-500" />
                涉及版权的转载与合作，请使用商务邮箱联系。
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <section className="mt-16">
        <SectionHeading
          eyebrow="FAQ"
          title="提交前先看看这些"
          description="多数问题在这里都有答案，看完能省下一次来回。"
        />
        <div className="mt-8">
          <FaqAccordion items={faqs.slice(0, 4)} />
        </div>
        <div className="mt-6">
          <Link
            to="/archive"
            className="group inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-600 transition-colors duration-200 hover:text-accent-400 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base transition-transform duration-200 group-hover:-translate-x-1" />
            返回复盘档案库
          </Link>
        </div>
      </section>
    </div>
  );
}